import MainContainer from '@/components/Shared/MainContainer';
import { PageWrapper } from '@/components/Shared/Wrapper';
import { useTranslation } from 'react-i18next';
import { useGetVoteResults } from '../api/getVotingResults';
import CommonTable from '@/components/Shared/CommonTable';
import { voteResultsColumns } from '../utils/VoteResultsColumns';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';
import { Flex } from 'antd';
import { useParams } from 'react-router-dom';
import VideoIcon from '@/components/Icons/VideoIcon';
import { useState } from 'react';
import { PieChartOutlined, TeamOutlined } from '@ant-design/icons';
import { StyledButton } from '@/lib/theme/components/Button';
import themeToken from '@/lib/theme/tokens';
import VotingResultsReportDownload from '../components/VotingResultsReportPdf';
import { useUserDataStore } from '@/stores/UserData';
import { useGetMeetingDetails } from '@/features/investorRelations/api/meetings/getMeetingDetails';

const VoteResults = () => {
  const { t, i18n } = useTranslation('vote', {
    keyPrefix: 'vote',
  });

  const { meetingID } = useParams<{ meetingID: string }>();
  const [isByOwnership, setIsByOwnership] = useState(true);

  const { selectedCompany } = useSelectedCompanyStore((state) => state);
  const { userData } = useUserDataStore((state) => state);

  const { data: meetingDetails } = useGetMeetingDetails({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? +meetingID : -1,
  });

  const { data: memberVoteResults, isPending: isMemberVoteResultsLoading } = useGetVoteResults({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? parseInt(meetingID) : undefined,
    isByOwnership: false,
  });

  const { data: ownershipVoteResults, isPending: isOwnershipVoteResultsLoading } = useGetVoteResults({
    companyID: selectedCompany?.cid as number,
    meetingID: meetingID ? parseInt(meetingID) : undefined,
    isByOwnership: true,
  });

  const voteResults = isByOwnership ? ownershipVoteResults : memberVoteResults;
  const isVoteResultsLoading = isByOwnership ? isOwnershipVoteResultsLoading : isMemberVoteResultsLoading;

  const columns = voteResultsColumns({
    isLoading: isVoteResultsLoading,
    t,
    isByOwnership,
  });

  return (
    <MainContainer
      title={t('voteResults')}
      breadcrumbItems={[
        {
          href: '/investor-relations/meetings',
          title: (
            <>
              <VideoIcon width={12} height={20} active />
              {t('meetings')}
            </>
          ),
        },
        {
          title: t('meetingDetails'),
          href: `/investor-relations/meetings/${meetingID}`,
        },
        { title: t('voteResults') },
      ]}
      headerContent={
        <VotingResultsReportDownload
          meetingName={meetingDetails?.data?.name || ''}
          companyID={selectedCompany?.cid as number}
          meetingID={meetingID ? parseInt(meetingID) : undefined}
          reportHeaderInfo={{
            company: {
              currency: '',
              name: selectedCompany?.name ?? '',
            },
            user: {
              email: userData?.userInfo.mail ?? '',
            },
          }}
          t={t}
          currentLanguage={i18n.language}
        />
      }
    >
      <PageWrapper>
        <Flex
          gap={themeToken.marginXS}
          wrap="wrap"
          style={{
            alignSelf: 'flex-end',
            marginBottom: themeToken.marginLG,
            padding: themeToken.paddingXXS,
            border: `1px solid ${themeToken.colorBorderSecondary}`,
            borderRadius: themeToken.borderRadiusLG,
            backgroundColor: themeToken.colorFillAlter,
          }}
        >
          <StyledButton
            type={isByOwnership ? 'primary' : 'default'}
            icon={<PieChartOutlined />}
            aria-pressed={isByOwnership}
            disabled={isVoteResultsLoading}
            onClick={() => setIsByOwnership(true)}
          >
            {t('showVoteByOwnership')}
          </StyledButton>
          <StyledButton
            type={!isByOwnership ? 'primary' : 'default'}
            icon={<TeamOutlined />}
            aria-pressed={!isByOwnership}
            disabled={isVoteResultsLoading}
            onClick={() => setIsByOwnership(false)}
          >
            {t('showVoteByMember')}
          </StyledButton>
        </Flex>
        {voteResults?.data && voteResults?.data.length > 0 ? (
          <CommonTable
            dataSource={voteResults.data}
            columns={columns}
            rowKey="id"
            pagination={false}
            loading={isVoteResultsLoading}
          />
        ) : (
          <Flex vertical align="center" justify="center" flex={1}>
            <h2>{t('noResults')}</h2>
          </Flex>
        )}
      </PageWrapper>
    </MainContainer>
  );
};

export default VoteResults;
